import { GetServerSideProps, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { useCallback, useMemo, useRef, useState } from 'react';
import ImageGallery from 'react-image-gallery';

import Button from '@components/common/Button/Button';
import CarImage from '@components/common/CarImage/CarImage';
import ErrorBlock from '@components/common/ErrorBlock/ErrorBlock';
import { LocationIcon } from '@components/common/Icons';
import MessageReceived from '@components/common/MessageReceived/MessageReceived';
import OtherCarsNearYouSection from '@components/common/OtherCarsNearYouSection/OtherCarsNearYouSection';
import QuestionFormContainerSection from '@components/common/QuestionFormContainerSection/QuestionFormContainerSection';
import RoundedTag from '@components/common/RoundedTag/RoundedTag';
import SectionWithContainer from '@components/common/SectionWithContainer/SectionWithContainer';
import SpecificationsSection from '@components/common/SpecificationsSection/SpecificationsSection';
import SubscriptionCustomisationCard from '@components/common/SubscriptionOptionCard/SubscriptionOptionCard';
import SubscriptionPlanBenefits from '@components/common/SubscriptionPlanBenefits/SubscriptionPlanBenefits';
import TabsWithContentCard from '@components/common/TabsWithContentCard/TabsWithContentCard';
import VehicleEnquireForm from '@components/common/VehicleEnquireForm/VehicleEnquireForm';
import { clientEnv } from '@config/client';
import { captureException } from '@sentry/nextjs';
import cx from 'classnames';
import EnquiryTypes from 'constants/EnquiryTypes';
import { Colors } from 'enums/colors';
import { LoopitProductType } from 'enums/loopitProductType';
import useVehicleProps from 'hooks/useVehicleProps';
import { pick } from 'lodash';
import xor from 'lodash/xor';
import { SubscriptionOption } from 'models/stack9/SubscriptionOption';
import { SubscriptionPlan } from 'models/SubscriptionPlan';
import { Vehicle } from 'models/Vehicle';

import RecaptchaContextProvider from 'contexts/recaptchaContext';
import carService from 'services/carService';
import subscriptionOptionService from 'services/subscriptionOptionService';
import subscriptionPlanService from 'services/subscriptionPlanService';
import formatToCurrency from 'utils/formatToCurrency';

import s from './vin.module.css';

type KMPack = {
  id: number;
  distance_allowance: number;
  price: string;
};
interface VehicleDetailPageProps {
  subscriptionPlans: SubscriptionPlan[];
  vehicle: Vehicle;
  subscriptionOptions: SubscriptionOption[];
}

export const getServerSideProps: GetServerSideProps<
  any,
  any,
  { vin: string }
> = async ({ params: { vin } }) => {
  try {
    const [subscriptionPlans, vehicle, subscriptionOptions] = await Promise.all(
      [
        subscriptionPlanService.getAllSubscriptions(),
        carService.getCarByVIN(vin),
        subscriptionOptionService.getAll(),
      ],
    );

    return {
      props: { subscriptionPlans, vehicle, subscriptionOptions },
    };
  } catch (error: any) {
    captureException(Error(`[Server-Side]: ${error.toString()}`));
    return { props: { vin, statusCode: 500 } };
  }
};

const VehicleDetailPage: NextPage<VehicleDetailPageProps> = ({
  vehicle,
  subscriptionPlans,
  subscriptionOptions,
}) => {
  const { priceTier1, priceTier2, priceTier3, title } =
    useVehicleProps(vehicle);
  const [selectedCustomisations, setSelectedCustomisations] = useState<
    number[]
  >([]);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [
    selectedSubscriptionPlanTabIndex,
    setSelectedSubscriptionPlanTabIndex,
  ] = useState<number>(0);
  const [showEnquireForm, setShowEnquireForm] = useState(false);
  const questionFormRef = useRef<HTMLDivElement | null>(null);

  const handleOnTabChange = useCallback(value => {
    setSelectedSubscriptionPlanTabIndex(value);
  }, []);

  const price = useMemo(() => {
    let roundedPrice;
    switch (selectedSubscriptionPlanTabIndex) {
      case 0:
        roundedPrice = priceTier1;
        break;
      case 1:
        roundedPrice = priceTier2;
        break;
      case 2:
        roundedPrice = priceTier3;
        break;
      default:
        roundedPrice = priceTier1;
        break;
    }
    return roundedPrice && Math.round(roundedPrice);
  }, [priceTier1, priceTier2, priceTier3, selectedSubscriptionPlanTabIndex]);

  const renderCardFooterPrice = useCallback((price: number) => {
    return (
      <div className="flex justify-end p-2 -mx-4 -mb-4 pr-7 bg-dark-blue-5 rounded-br-3xl rounded-bl-sm">
        <h3 className="flex items-start font-bold transition-colors text-dark-blue-100">
          <span className="mt-1 mr-1 text-sm">$</span>
          <div className="flex items-end">
            <span className="block mr-1 -mb-1">{price}</span>
            <span
              className={cx(
                'block text-sm font-normal transition-colors text-blue-100',
              )}
            >
              per week
            </span>
          </div>
        </h3>
      </div>
    );
  }, []);

  const openLoopit = useCallback(
    formValue => {
      let kmPackId;

      const kmBoosterPlan = subscriptionOptions.find(
        opt => opt.loopit_product === LoopitProductType.KM_BOOSTER_PLAN,
      );
      const liabilityProtectionPlan = subscriptionOptions.find(
        opt => opt.loopit_product === LoopitProductType.LIABILITY_PROTECTION,
      );

      const hasKM = kmBoosterPlan
        ? selectedCustomisations.includes(kmBoosterPlan?.id)
        : false;

      const hasLiabilityProtection = liabilityProtectionPlan
        ? selectedCustomisations.includes(liabilityProtectionPlan?.id)
        : false;

      if (kmBoosterPlan && hasKM && vehicle.km_packs) {
        const packs = JSON.stringify(vehicle.km_packs); // Field contains a JSON but TS reads as string
        kmPackId = JSON.parse(packs).find((pack: KMPack) => {
          return pack.distance_allowance === kmBoosterPlan.loopit_km;
        });
      }

      const params = new URLSearchParams({
        source: vehicle.loopit_source || '',
        stock_no: vehicle.stock_no,
        vin: vehicle.vin,
        first_name: formValue.first_name,
        last_name: formValue.last_name,
        email: formValue.email,
        phone: formValue.phone,
        plan_id: (selectedSubscriptionPlanTabIndex + 1).toString(),
        liability_protection: hasLiabilityProtection ? '1' : '0',
        ...(kmPackId ? { km_pack_id: kmPackId.id } : {}),
        ...(formValue['blinker-checkbox'] ? { 'blinker-checkbox': 'on' } : {}),
      });

      window.open(`${clientEnv()?.LOOPIT_URL}?${params}`, '_blank');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicle.loopit_source, vehicle.vin, selectedCustomisations],
  );

  return !vehicle.id ? (
    <ErrorBlock statusCode={404} />
  ) : (
    <RecaptchaContextProvider>
      <NextSeo title={title + ' for Subscription'} description={title} />
      <SectionWithContainer className="border-t-2 border-blue-60">
        <div className="grid grid-cols-6 space-y-6 lg:space-x-16 lg:space-y-0">
          <div
            className={cx(
              'col-span-6 lg:col-span-4 relative',
              s.imageGalleryWrapper,
            )}
          >
            {vehicle.vehicle_images?.length ? (
              <>
                {!!vehicle.reduced_banner && (
                  <RoundedTag className="absolute left-0 top-0 z-10">
                    PRICE REDUCED
                  </RoundedTag>
                )}
                <ImageGallery
                  items={vehicle.vehicle_images.map(
                    ({ ad_image, image_url }) => ({
                      original: ad_image?.url || image_url || '',
                      thumbnail: ad_image?.url || image_url || '',
                      originalHeight: 300,
                    }),
                  )}
                />
              </>
            ) : (
              <CarImage
                showReducedBanner={!!vehicle.reduced_banner}
                className="object-cover object-center w-full h-[634px]"
                alt={title}
                src={'/images/Car.svg'}
              />
            )}
          </div>
          <div className="col-span-6 lg:col-span-2">
            <h1 className="text-3xl font-light text-dark-blue-100">{title}</h1>
            {vehicle.suburb && vehicle.state?.name && (
              <div className="flex my-3">
                <span className="mr-1 text-coral-100">
                  <LocationIcon className="h-5 w-5 text-coral-100 mr-3" />
                </span>
                <span>{`${vehicle.suburb}, ${vehicle.state?.name}`}</span>
              </div>
            )}
            <div className="flex justify-between mb-5">
              {price && (
                <h3 className="flex items-start font-bold transition-colors text-dark-blue-100">
                  <span className="mt-1 mr-1 text-sm">$</span>
                  <div className="flex items-end">
                    <span className="block mr-1 -mb-1">{price}</span>
                    <span
                      className={cx(
                        'block text-sm font-normal transition-colors text-blue-100',
                      )}
                    >
                      per week
                    </span>
                  </div>
                </h3>
              )}
              {!showEnquireForm && (
                <Button
                  color={Colors.green}
                  className="px-9"
                  onClick={() => setShowEnquireForm(true)}
                >
                  SUBSCRIBE
                </Button>
              )}
              {showEnquireForm && (
                <Button
                  outline
                  color={Colors.green}
                  className="px-5"
                  onClick={() => setShowEnquireForm(false)}
                >
                  BACK TO SUMMARY
                </Button>
              )}
            </div>
            {!showEnquireForm && (
              <TabsWithContentCard
                cardProps={{
                  withShadow: false,
                  borderRadius: [false, false, '3xl', 'sm'],
                  className: 'shadow-card p-4 mb-4 bg-white',
                }}
                btnProps={{
                  className: 'uppercase',
                }}
                tabs={subscriptionPlans.map(
                  ({ name, subscription_plan_benefits }) => ({
                    label: name,
                    component: (
                      <div>
                        <SubscriptionPlanBenefits
                          featuredText
                          subscriptionPlanBenefits={subscription_plan_benefits}
                        />
                        {price && renderCardFooterPrice(price)}
                      </div>
                    ),
                  }),
                )}
                value={selectedSubscriptionPlanTabIndex}
                onChange={handleOnTabChange}
              />
            )}
            {showEnquireForm && (
              <>
                <h4 className="mb-3 font-light">
                  Subscribe today and drive away!
                </h4>
                <VehicleEnquireForm
                  onSubmit={(value: any) => {
                    openLoopit(value);
                  }}
                />
              </>
            )}
            {!showEnquireForm && (
              <>
                <div className="flex flex-col space-y-3 lg:space-y-0 lg:items-center lg:flex-row lg:space-x-3 justify-evenly">
                  <div className="flex-1">
                    <Button
                      className="block w-full"
                      color={Colors.green}
                      onClick={() => setShowEnquireForm(true)}
                    >
                      SUBSCRIBE
                    </Button>
                  </div>
                  <div className="flex-1">
                    <Button
                      className="block w-full"
                      color={Colors.green}
                      onClick={() => {
                        if (questionFormRef?.current) {
                          window.scrollTo({
                            behavior: 'smooth',
                            top: questionFormRef.current.offsetTop,
                          });
                        }
                      }}
                      outline
                    >
                      ENQUIRE
                    </Button>
                  </div>
                </div>
                {!!price && (
                  <p className="mt-3 text-sm text-grey-60">
                    Establishment fee is non-refundable except in certain
                    circumstances. Total minimal cost is{' '}
                    {formatToCurrency(
                      Math.round((price / 7) * 120 * 100) / 100,
                    )}{' '}
                    over 120 days.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </SectionWithContainer>
      <SectionWithContainer>
        <SpecificationsSection
          vehicle={pick(
            vehicle,
            'body_type',
            'fuel_type',
            'transmission_type',
            'engine_size',
            'seat_count',
            'drive_type',
            'glass_features',
            'stock_no',
            'glass_data',
            'odometer',
          )}
        />
      </SectionWithContainer>
      <SectionWithContainer className="border-t border-t-blue-60">
        <h2 className="mb-3 font-light text-center text-dark-blue-100">
          Customise your subscription
        </h2>
        <p className="mb-8 text-xl text-center">
          Select from these additional options to make your subscription work
          for you
        </p>
        <div className="max-w-[50rem] mx-auto">
          {subscriptionOptions.map(subscriptionOption => (
            <SubscriptionCustomisationCard
              key={subscriptionOption.id}
              className="mb-7"
              selected={selectedCustomisations.includes(subscriptionOption.id)}
              onChange={() => {
                setSelectedCustomisations(
                  xor(selectedCustomisations, [subscriptionOption.id]),
                );
              }}
              subscriptionOption={subscriptionOption}
            />
          ))}
        </div>
      </SectionWithContainer>
      <OtherCarsNearYouSection />
      {submitted ? (
        <MessageReceived />
      ) : (
        <div ref={questionFormRef}>
          <QuestionFormContainerSection
            enquiryType={EnquiryTypes.VehicleEnquiry}
            vehicleVin={vehicle.vin}
            onSubmit={val => {
              setSubmitted(!!val);
            }}
          />
        </div>
      )}
    </RecaptchaContextProvider>
  );
};

export default VehicleDetailPage;
